import React, { type ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { style } from 'typestyle';
import MenuIcon from 'mdi-react/MenuIcon';
import Column from '../column/column.component';
import Menu from '../menu/menu.component';
import Row from '../row/row.component';
import * as styles from './page.style';
import { IsMobileContext } from '../../context/is-mobile';
import StyleConstants from '../../styles.constants';

interface Props {
  readonly children: ReactElement;
}

const Page = ({ children }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const [isReady, setIsReady] = useState(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, [menuOpen]);
  const toggleMenu = useCallback(() => {
    setMenuOpen((open) => !open);
  }, [menuOpen]);

  const wrapperRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    Promise.all([
      document.fonts.load(`${StyleConstants.HEADER_FONT_SIZE}px ${StyleConstants.HEADER_FONT_FAMILY}`),
      document.fonts.load(`${StyleConstants.PRIMARY_FONT_SIZE}px ${StyleConstants.PRIMARY_FONT_FAMILY}`),
    ]).then(() => {
      setIsReady(true);
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (wrapperRef.current != null && !wrapperRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [wrapperRef]);

  return isReady ? (
    <Column className={style(styles.page)}>
      {isMobile ? (
        <div className={style(styles.mobile)}>
          <div className={style(styles.scrollContainer)}>
            <div className={style(styles.scrollable)}>
              <div ref={wrapperRef}>
                <MenuIcon className={style(styles.menuToggle)} onClick={(): void => toggleMenu()} />
              </div>
              {children}
            </div>
          </div>
          <div>
            <Menu open={menuOpen} closeMenu={closeMenu} />
          </div>
        </div>
      ) : (
        <Row className={style(styles.row)}>
          <Menu open closeMenu={closeMenu} />
          <div className={style(styles.scrollContainer)}>
            <div className={style(styles.scrollable)}>{children}</div>
          </div>
        </Row>
      )}
    </Column>
  ) : (
    <div />
  );
};

export default Page;
